import type { SipIncreaseRecommendation } from '../../../../api/sipPlan.types'
import { SipCategoryBucketKey } from '../../../../api/types'
import {
  PlansFundCard,
  type PlanFundCardCta
} from '../../../../../lumpsum/plans-list/lumpsum-ui-components/PlansFundCard'
import { useIncreaseSipAction } from '../../../../../execution/sip/increase/usePlanIncreaseSipAction'
import { useOpenSipPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenSipPlanRecommendationBottomSheet'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import { formatTargetSip } from '../../../../../display-values/sipPlanAmount'
import { useSipPlansContext } from '../../../sipPlansContext'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'
import {
  SipAmountBadge,
  SipFundCardTopRightLabel
} from './sipYourPlanFundCardShared'

type IncreaseSipFundCardProps = {
  recommendation: SipIncreaseRecommendation
  showBottomDivider?: boolean
}

export function IncreaseSipFundCard({
  recommendation,
  showBottomDivider = true
}: IncreaseSipFundCardProps) {
  const { plansOverview } = useSipPlansContext()
  const { openSipPlanRecommendationBottomSheet } = useOpenSipPlanRecommendationBottomSheet()
  const { onIncreaseSip } = useIncreaseSipAction()
  const totalIdealSipAmount = plansOverview?.totalIdealSipAmount ?? 0
  const isin = recommendation?.isin ?? ''
  const fundName = recommendation?.fundName ?? ''
  const screenName = PlansScreen.SipYourPlan
  const fundRole = PlansFundRole.IncreaseSip
  const currentSipAmount = recommendation?.currentSipAmount ?? 0
  const recommendedSipAmount = recommendation?.recommendedSipAmount ?? 0
  const formattedTargetSip = formatTargetSip({ rawAmount: recommendedSipAmount })
  const formattedCurrentSip = showAsFormattedRupeeAmount({ rawAmount: currentSipAmount })

  const openPlanBottomSheet = () => {
    openSipPlanRecommendationBottomSheet({
      isin,
      totalIdealSipAmount,
      bucketType: SipCategoryBucketKey.increaseRecommendations,
      recommendation,
      initiateSipCta: plansOverview?.initiateSipCta
    })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const handleEditSip = () => {
    trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Edit SIP', fundName, fundIsin: isin })
    onIncreaseSip(recommendation, plansOverview?.initiateSipCta)
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-edit-sip`,
      label: 'Edit SIP',
      onClick: handleEditSip,
      variant: 'tertiary'
    },
    {
      id: `${isin}-learn-more`,
      label: 'Learn more',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Learn more', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: recommendation?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: recommendation?.fundNature ?? '',
        category: recommendation?.fundCategory ?? '',
        purchaseMode: recommendation?.purchaseMode ?? ''
      }}
      topRight={<SipFundCardTopRightLabel label="Increase to" targetSipAmount={formattedTargetSip} />}
      badgeRowExtras={currentSipAmount > 0 ? <SipAmountBadge currentSipAmount={formattedCurrentSip} /> : null}
      status={{
        rank: recommendation?.powerRank ?? 0,
        rating: recommendation?.powerRating ?? ''
      }}
      recommendationStatus={recommendation?.recommendationStatus}
      recommendationStatusDisplayText={recommendation?.recommendationStatusDisplayText}
      ctas={ctas}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}
